import * as Sentry from "@sentry/react";

/**
 * Log an error to Sentry
 * @param {Error} error - The error to log
 * @param {Object} context - Additional context about the error
 */
export const logError = (error, context = {}) => {
    if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.captureException(error, {
            extra: context,
        });
    } else {
        console.error('Error:', error, context);
    }
};

/**
 * Log a warning/message to Sentry
 * @param {string} message - The warning message
 * @param {Object} context - Additional context
 */
export const logWarning = (message, context = {}) => {
    if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.captureMessage(message, {
            level: 'warning',
            extra: context,
        });
    } else {
        console.warn('Warning:', message, context);
    }
};

/**
 * Log a breadcrumb (user action for context)
 * @param {string} message - The breadcrumb message
 * @param {string} category - Category of the action (e.g., 'navigation', 'user-action')
 * @param {Object} data - Additional data
 */
export const logBreadcrumb = (message, category = 'user-action', data = {}) => {
    if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.addBreadcrumb({
            message,
            category,
            level: 'info',
            data,
        });
    } else {
        console.log(`[Breadcrumb] ${category}:`, message, data);
    }
};

/**
 * Set user context (privacy-safe)
 * @param {string} userId - User ID (hashed/anonymized)
 * @param {string} username - Username (no email or PII)
 */
export const setUserContext = (userId, username) => {
    if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.setUser({
            id: userId,
            username: username, // No email or PII
        });
    }
};

/**
 * Clear user context (on logout)
 */
export const clearUserContext = () => {
    if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.setUser(null);
    }
};

/**
 * Start a performance transaction
 * @param {string} name - Name of the transaction
 * @param {string} operation - Operation type (e.g., 'component', 'http')
 * @returns {Transaction} Sentry transaction object (call .finish() when done)
 */
export const startTransaction = (name, operation) => {
    // Sentry v8+ removed startTransaction. returning a no-op object preventing build warnings/runtime errors.
    return {
        finish: () => { },
        setStatus: () => { },
        setData: () => { },
    };
};
